import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatColorText,
  FormatSize,
  InsertLink,
  InsertEmoticon,
  Image as ImageIcon,
  Lock as LockIcon,
  WarningAmber as WarnIcon,
  Block as BlockIcon,
  PersonAddAlt1 as AddBuddyIcon,
  Email as SendMailIcon,
} from '@mui/icons-material';
import { renderRichText, resolveUsernameColor } from '../utils/richText';
import { formatClockTime, formatRelativeTime } from '../utils/formatTime';
import { fontSizeRem } from '../utils/displayPrefs';

// =============================================================================
// ImConversation — the CONTENT of an IM window: a running message log
// (name-prefixed colored lines + timestamps), an "is typing…" line, and the
// compose box. It is pure window CONTENT — no frame, drag, resize, or title bar.
// The generic window manager (windowing/Window.js) provides all chrome; this
// component just fills the body. That keeps window-management logic fully
// separate from IM content.
//
// Two skins share this component:
//   - the default theme-aware skin (uses palette.custom.* tokens), and
//   - a "classic" AOL-Instant-Messenger skin (`classic` prop) — a retro
//     Windows-XP look (menu bar, white message log, format toolbar, beige
//     Send) that DELIBERATELY ignores the app theme. The format toolbar and
//     File/Edit/Insert/People menu are decorative only (no behavior yet); the
//     greyed icons (image, lock) stand in for features we don't expose here.
//
// Props:
//   partner          { id, username, usernameColor?, role? }
//   messages         [{ id, mine, content, type, away?, timestamp }]
//   typing           bool — partner is typing at me
//   active           bool — this window is focused (autofocus the compose box)
//   myUsername       string — labels my own lines
//   myColor          string — my username color
//   fontSize         display-pref font-size pct (matches main chat)
//   timestampFormat  'clock' | 'relative' | 'off'
//   classic          bool — render the retro AIM skin (overrides app theme)
//   soundsOn         bool — AIM message sounds on (checkable in the Edit menu)
//   onToggleSounds(on) toggle the AIM message sounds
//   onSend(text)     send a private message
//   onTyping(bool)   local typing started/stopped (relayed to the partner)
// =============================================================================

const TYPING_STOP_MS = 2500; // idle gap after which we tell the peer we stopped

// Classic-skin menu-bar entries (decorative). Mirrors a classic Windows app
// menu order (File · Edit · View · Insert); "Insert" maps to the format strip.
const CLASSIC_MENUS = ['File', 'Edit', 'View', 'Insert'];

// Classic-skin compose-area resize bounds (px). The user drags the separator
// above the input to size it; the message log absorbs the difference.
const CLASSIC_COMPOSE_MIN_H = 80;
const CLASSIC_COMPOSE_MAX_H = 240;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  log: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1, 1.25),
    background: theme.palette.custom.chatBg,
  },
  line: {
    margin: theme.spacing(0.35, 0),
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  name: {
    fontWeight: 700,
    marginRight: 4,
  },
  time: {
    color: theme.palette.custom.inkFaint,
    fontSize: '0.7em',
    marginLeft: 6,
  },
  awayLine: {
    fontStyle: 'italic',
    color: theme.palette.custom.inkDim,
  },
  awayTag: {
    fontStyle: 'normal',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.custom.ember,
    marginRight: 4,
  },
  typing: {
    height: 16,
    padding: theme.spacing(0, 1.25),
    fontSize: '0.72rem',
    fontStyle: 'italic',
    color: theme.palette.custom.inkFaint,
    flexShrink: 0,
  },
  composeRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75),
    borderTop: `1px solid ${theme.palette.custom.line}`,
    background: theme.palette.custom.composerBoxBg,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    resize: 'none',
    maxHeight: 90,
    minHeight: 34,
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.custom.line}`,
    background: theme.palette.background.paper,
    color: theme.palette.custom.ink,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    outline: 'none',
    '&:focus': { borderColor: theme.palette.primary.main },
  },
  sendBtn: {
    flexShrink: 0,
    padding: theme.spacing(0.85, 1.5),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    fontFamily: 'inherit',
    fontWeight: 700,
    fontSize: '0.8rem',
    cursor: 'pointer',
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },

  // ===========================================================================
  // CLASSIC (AOL Instant Messenger) skin. Self-contained, hard-coded retro
  // colors — intentionally theme-independent so the toggle looks the same under
  // any app theme. Tahoma/MS-Sans-Serif font, white message canvas, a beige
  // Win-XP menu bar + format toolbar, and a 3D beige Send button.
  // ---------------------------------------------------------------------------
  classicRoot: {
    background: '#ece9d8', // XP "Luna" face color
    fontFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", sans-serif',
  },
  // The File / Edit / View / Insert bar. Only Edit opens (the sound toggle);
  // the rest are stubs. position:relative anchors the dropdown.
  classicMenuBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
    padding: '1px 2px',
    background: '#ece9d8',
    borderBottom: '1px solid #aca899',
    fontSize: '11px',
    color: '#000',
    userSelect: 'none',
  },
  classicMenuItem: {
    padding: '2px 7px',
    borderRadius: 2,
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    color: 'inherit',
    cursor: 'default',
    // Highlight on hover; the open menu stays highlighted (classicMenuItemOpen).
    '&:hover': { background: '#bfd9fb', outline: '1px solid #3c7fb1' },
  },
  classicMenuItemOpen: {
    background: '#bfd9fb',
    outline: '1px solid #3c7fb1',
  },
  // A dropdown panel hanging off a menu-bar item: a raised XP menu with a sunken
  // border, positioned absolutely under the menu bar.
  classicMenuDropdown: {
    position: 'absolute',
    top: '100%',
    zIndex: 5,
    minWidth: 168,
    padding: 2,
    background: '#fff',
    border: '1px solid',
    borderColor: '#fff #808080 #808080 #fff',
    boxShadow: '2px 2px 3px rgba(0,0,0,0.3)',
    fontSize: '12px',
  },
  classicMenuRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '4px 8px 4px 22px',
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    color: '#000',
    textAlign: 'left',
    cursor: 'default',
    position: 'relative',
    '&:hover': { background: '#316ac5', color: '#fff' },
  },
  // The leading ✓ slot for a checkable item (shown only when checked).
  classicMenuCheck: {
    position: 'absolute',
    left: 6,
    fontWeight: 700,
  },
  // Sunken white message canvas (the AIM "history" pane).
  classicLog: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '6px 8px',
    margin: 4,
    background: '#fff',
    border: '2px solid',
    // Classic sunken bevel: dark top-left, light bottom-right.
    borderColor: '#7f9db9 #fff #fff #7f9db9',
    color: '#000',
    lineHeight: 1.45,
    // Override the app's GLOBAL dark scrollbar (white-on-white, invisible here)
    // with a chunky grey classic Windows scrollbar that reads on the white pane.
    scrollbarWidth: 'auto', // Firefox
    scrollbarColor: '#c0c0c0 #ece9d8',
    '&::-webkit-scrollbar': { width: 16, height: 16 },
    '&::-webkit-scrollbar-track': {
      background: '#ece9d8',
      border: '1px solid #d4d0c8',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c0c0c0',
      border: '1px solid #fff',
      borderRadius: 0,
      boxShadow: 'inset -1px -1px 0 #808080, inset 1px 1px 0 #dfdfdf',
    },
    '&::-webkit-scrollbar-thumb:hover': { background: '#b0b0b0' },
  },
  // Message text is black on white; names keep the user's color (set inline).
  // The classic skin shows the timestamp inline in the name prefix
  // (`name (time):`), so it needs no separate trailing-time class.
  classicAwayTag: {
    fontStyle: 'normal',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#c0651a',
    marginRight: 4,
  },
  classicTyping: {
    height: 15,
    padding: '0 8px',
    fontSize: '11px',
    fontStyle: 'italic',
    color: '#555',
    flexShrink: 0,
  },
  // The grey format toolbar that sits above the input (A A, B I U, link, …).
  classicToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
    padding: '2px 4px',
    margin: '0 4px',
    background: '#ece9d8',
    borderTop: '1px solid #fff',
    borderBottom: '1px solid #aca899',
  },
  classicToolBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 20,
    border: '1px solid transparent',
    borderRadius: 2,
    color: '#3a3a3a',
    cursor: 'default',
    '& svg': { fontSize: 15 },
    // Raised-on-hover (toolbar button) feel.
    '&:hover': {
      background: '#fbfaf6',
      borderColor: '#aca899 #fff #fff #aca899',
    },
  },
  // Greyed/unsupported toolbar buttons (e.g. image upload, encryption "lock").
  classicToolBtnDisabled: {
    color: '#aca899',
    '&:hover': { background: 'transparent', borderColor: 'transparent' },
  },
  classicToolSep: {
    width: 1,
    height: 16,
    margin: '0 3px',
    background: '#aca899',
    borderRight: '1px solid #fff',
  },
  // Draggable horizontal separator between the toolbar and the compose box.
  // Grabbing it resizes the input area (and shrinks the log). Styled as a thin
  // raised XP rail with a centered dotted gripper, ns-resize cursor.
  classicResizeHandle: {
    flexShrink: 0,
    height: 6,
    margin: '0 4px',
    cursor: 'ns-resize',
    background: '#ece9d8',
    borderTop: '1px solid #fff',
    borderBottom: '1px solid #aca899',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'none', // let pointer drag work on touch without scrolling
    '&::after': {
      // The little center gripper dots, like a Windows splitter grip.
      content: '""',
      width: 24,
      height: 2,
      borderTop: '1px dotted #808080',
    },
    '&:hover': { background: '#f3f1e6' },
  },
  // White compose area with a sunken bevel matching the log.
  classicComposeRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 6,
    flexShrink: 0,
    padding: 4,
    boxSizing: 'border-box', // composeH is the true outer height (incl. padding)
    background: '#ece9d8',
  },
  classicInput: {
    flex: 1,
    resize: 'none',
    height: '100%', // sized by the compose row (driven by composeH)
    padding: '4px 5px',
    background: '#fff',
    border: '2px solid',
    borderColor: '#7f9db9 #fff #fff #7f9db9',
    color: '#000',
    fontFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", sans-serif',
    fontSize: '12px',
    outline: 'none',
  },
  // Bottom action bar (AIM's Warn / Block / Add Buddy … Send row). A beige strip
  // below the compose box: icon-over-label action buttons on the left, the big
  // Send button anchored right. Sits on a raised XP rail.
  classicActionBar: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 6,
    flexShrink: 0,
    padding: '5px 6px',
    background: '#ece9d8',
    borderTop: '1px solid #fff',
  },
  // A beveled group box that fences related action buttons (AIM groups its
  // bottom buttons with separator edges: [Warn · Block] | [Add Buddy] | …).
  // Raised XP bevel: light top-left, dark bottom-right.
  classicActionGroup: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 2,
    padding: '2px 4px',
    borderRadius: 3,
    border: '1px solid',
    borderColor: '#fff #808080 #808080 #fff',
    background: '#ece9d8',
  },
  // An icon-over-label action button (Warn / Block / Add Buddy). Flat until
  // hover, then raised — the classic toolbar-button feel.
  classicActionBtn: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    minWidth: 52,
    padding: '3px 6px',
    border: '1px solid transparent',
    borderRadius: 3,
    background: 'transparent',
    color: '#1a1a1a',
    fontFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", sans-serif',
    fontSize: '11px',
    cursor: 'pointer',
    '& svg': { fontSize: 22, color: '#c0651a' }, // warm AIM-ish icon tint
    '&:hover': {
      borderColor: '#aca899 #fff #fff #aca899',
      background: '#fbfaf6',
    },
    '&:active': { borderColor: '#808080 #fff #fff #808080', background: '#e3e0d2' },
  },
  // Warn is greyed (no functionality) — desaturated icon + muted label, inert.
  classicActionBtnDisabled: {
    color: '#9a9a8f',
    cursor: 'default',
    '& svg': { color: '#aca899' },
    '&:hover': { borderColor: 'transparent', background: 'transparent' },
    '&:active': { borderColor: 'transparent', background: 'transparent' },
  },
  // Pushes the Send button to the right edge of the action bar.
  classicActionSpacer: { flex: 1 },
  // The big 3D beige Send command button (icon + label). No status meter beneath.
  classicSendBtn: {
    flexShrink: 0,
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    minWidth: 72,
    padding: '4px 14px',
    background: 'linear-gradient(#fdfdfb, #ece9d8)',
    border: '1px solid #707070',
    borderRadius: 3,
    boxShadow: 'inset 0 0 0 1px #fff',
    color: '#000',
    fontFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", sans-serif',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    '& svg': { fontSize: 20, color: '#3a5fb0' }, // envelope, blue like the reference
    '&:hover': { background: 'linear-gradient(#fff, #f3f1e6)' },
    '&:active': {
      background: '#e3e0d2',
      boxShadow: 'inset 1px 1px 2px rgba(0,0,0,0.25)',
    },
    '&:disabled': {
      color: '#9a9a8f',
      background: '#ece9d8',
      boxShadow: 'inset 0 0 0 1px #fff',
      cursor: 'default',
      '& svg': { color: '#aca899' },
    },
  },
}));

const ImConversation = ({
  partner,
  messages = [],
  typing = false,
  active = false,
  myUsername = 'You',
  myColor,
  fontSize,
  timestampFormat = 'clock',
  classic = false,
  soundsOn = true,
  onToggleSounds,
  onSend,
  onTyping,
}) => {
  const { classes, cx } = useStyles();
  const [draft, setDraft] = useState('');
  const logRef = useRef(null);
  const inputRef = useRef(null);
  const typingRef = useRef(false);
  const typingTimer = useRef(null);
  // Classic menu-bar dropdown: which top menu is open (null = none). Only the
  // Edit menu has real items so far (the AIM sound toggle); the rest are stubs.
  const [openMenu, setOpenMenu] = useState(null);
  const menuBarRef = useRef(null);

  // Close the open menu on any outside click or Escape.
  useEffect(() => {
    if (!openMenu) return undefined;
    const onDocDown = (e) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  // Classic skin only: a draggable separator above the compose box lets the
  // user grow the input area (the log shrinks to compensate, since the window
  // height is fixed). `composeH` is the explicit compose-area height in px;
  // the drag tracks pointer Y from a captured start point.
  const [composeH, setComposeH] = useState(CLASSIC_COMPOSE_MIN_H);
  const resizeStart = useRef(null); // { y, h } captured on pointerdown

  const onResizeDown = (e) => {
    // Left button / primary pointer only.
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    resizeStart.current = { y: e.clientY, h: composeH };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw if the pointer is already gone — ignore */
    }
  };

  const onResizeMove = (e) => {
    const start = resizeStart.current;
    if (!start) return;
    // Drag UP (clientY decreases) grows the input; clamp to sane bounds.
    const next = start.h + (start.y - e.clientY);
    setComposeH(
      Math.max(CLASSIC_COMPOSE_MIN_H, Math.min(CLASSIC_COMPOSE_MAX_H, next))
    );
  };

  const onResizeUp = (e) => {
    resizeStart.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // Auto-scroll to the newest line whenever the log grows or the partner starts
  // typing (keeps the latest in view, like the main chat).
  useLayoutEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, typing]);

  // Focus the compose box when this window becomes the active one.
  useEffect(() => {
    if (active && inputRef.current) inputRef.current.focus();
  }, [active]);

  // Tell the peer we stopped typing if we unmount mid-compose. Capture the
  // mutable refs into locals so the cleanup doesn't read .current at teardown.
  useEffect(() => {
    const wasTyping = typingRef;
    const timer = typingTimer;
    const notify = onTyping;
    return () => {
      clearTimeout(timer.current);
      if (wasTyping.current) {
        wasTyping.current = false;
        notify && notify(false);
      }
    };
    // Intentionally run once for the lifetime of the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtTime = (ts) => {
    if (timestampFormat === 'off' || !ts) return '';
    return timestampFormat === 'relative'
      ? formatRelativeTime(ts) || 'now'
      : formatClockTime(ts) || '';
  };

  const signalTyping = () => {
    if (!onTyping) return;
    if (!typingRef.current) {
      typingRef.current = true;
      onTyping(true);
    }
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      typingRef.current = false;
      onTyping(false);
    }, TYPING_STOP_MS);
  };

  const stopTyping = () => {
    clearTimeout(typingTimer.current);
    if (typingRef.current) {
      typingRef.current = false;
      onTyping && onTyping(false);
    }
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onSend && onSend(text);
    setDraft('');
    stopTyping();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const msgFontSize = fontSizeRem(fontSize);
  const partnerName = partner?.username || 'user';

  // One message line — shared by both skins. The caller passes the class set to
  // use; the name keeps the user's color in either skin (per the design: ignore
  // AIM's red/blue, use our username colors). When cls.inlineTime is set (the
  // classic skin), the timestamp rides inside the name prefix as
  // `name (time):` instead of trailing the message; either way it honors the
  // timestamp pref, so an 'off' pref drops the time and yields a plain `name:`.
  const renderLine = (m, cls) => {
    const mine = m.mine;
    const name = mine ? myUsername : partnerName;
    // Resolve like the main chat: a chosen color wins, otherwise fall back to
    // the role default (so a guest with no color isn't rendered white).
    const color = mine
      ? resolveUsernameColor(myColor, 'member')
      : resolveUsernameColor(partner?.usernameColor, partner?.role);
    const time = fmtTime(m.timestamp);
    return (
      <div key={m.id} className={cx(classes.line, m.away && cls.awayLine)}>
        {m.away && <span className={cls.awayTag}>Auto&nbsp;response</span>}
        <span className={classes.name} style={{ color }}>
          {cls.inlineTime && time ? `${name} (${time}):` : `${name}:`}
        </span>
        {renderRichText(m.content)}
        {!cls.inlineTime && time && <span className={cls.time}>{time}</span>}
      </div>
    );
  };

  // --- Classic (AOL Instant Messenger) skin -------------------------------
  if (classic) {
    // Decorative format toolbar. enabled:false renders a greyed, inert button
    // (image / encryption "lock") — placeholders for things this window can't
    // do yet. The rest are equally inert in this pass (pure look).
    const tools = [
      { key: 'size', icon: <FormatSize />, title: 'Font size' },
      { key: 'color', icon: <FormatColorText />, title: 'Text color' },
      { sep: true, key: 'sep1' },
      { key: 'bold', icon: <FormatBold />, title: 'Bold' },
      { key: 'italic', icon: <FormatItalic />, title: 'Italic' },
      { key: 'underline', icon: <FormatUnderlined />, title: 'Underline' },
      { sep: true, key: 'sep2' },
      { key: 'link', icon: <InsertLink />, title: 'Insert link' },
      { key: 'emoji', icon: <InsertEmoticon />, title: 'Insert emoticon' },
      { key: 'image', icon: <ImageIcon />, title: 'Insert image', enabled: false },
      { key: 'lock', icon: <LockIcon />, title: 'Encrypt (unavailable)', enabled: false },
    ];
    return (
      <div className={cx(classes.root, classes.classicRoot)}>
        <div className={cx(classes.classicMenuBar, 'wm-no-drag')} ref={menuBarRef}>
          {CLASSIC_MENUS.map((m) => (
            <button
              key={m}
              type="button"
              className={cx(
                classes.classicMenuItem,
                openMenu === m && classes.classicMenuItemOpen
              )}
              // Only Edit has a real menu; others toggle nothing (stub).
              onClick={() =>
                setOpenMenu((cur) => (m === 'Edit' ? (cur === 'Edit' ? null : 'Edit') : null))
              }
            >
              {m}
            </button>
          ))}
          {openMenu === 'Edit' && (
            <div className={classes.classicMenuDropdown} role="menu">
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={!!soundsOn}
                className={classes.classicMenuRow}
                onClick={() => {
                  onToggleSounds && onToggleSounds(!soundsOn);
                  setOpenMenu(null);
                }}
              >
                {soundsOn && <span className={classes.classicMenuCheck}>✓</span>}
                Play sounds
              </button>
            </div>
          )}
        </div>

        <div
          className={cx(classes.classicLog, 'wm-no-drag')}
          ref={logRef}
          style={{ fontSize: msgFontSize }}
        >
          {messages.map((m) =>
            renderLine(m, {
              awayLine: classes.awayLine,
              awayTag: classes.classicAwayTag,
              inlineTime: true,
            })
          )}
        </div>

        <div className={classes.classicTyping} aria-live="polite">
          {typing ? `${partnerName} is typing…` : ''}
        </div>

        <div className={cx(classes.classicToolbar, 'wm-no-drag')}>
          {tools.map((t) =>
            t.sep ? (
              <span key={t.key} className={classes.classicToolSep} />
            ) : (
              <span
                key={t.key}
                className={cx(
                  classes.classicToolBtn,
                  t.enabled === false && classes.classicToolBtnDisabled
                )}
                title={t.title}
                aria-hidden="true"
              >
                {t.icon}
              </span>
            )
          )}
        </div>

        <div
          className={cx(classes.classicResizeHandle, 'wm-no-drag')}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize message box"
          title="Drag to resize the message box"
        />

        <div
          className={cx(classes.classicComposeRow, 'wm-no-drag')}
          style={{ height: composeH }}
        >
          <textarea
            ref={inputRef}
            className={classes.classicInput}
            value={draft}
            placeholder={`Message ${partnerName}…`}
            onChange={(e) => {
              setDraft(e.target.value);
              if (e.target.value.trim()) signalTyping();
              else stopTyping();
            }}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className={cx(classes.classicActionBar, 'wm-no-drag')}>
          {/* Decorative AIM action buttons (no behavior yet), fenced into
              beveled groups like the original. Warn is greyed since we have no
              warn feature; Block / Add Buddy are inert. */}
          <div className={classes.classicActionGroup}>
            <span
              className={cx(classes.classicActionBtn, classes.classicActionBtnDisabled)}
              title="Warn (unavailable)"
              aria-hidden="true"
            >
              <WarnIcon />
              Warn
            </span>
            <span className={classes.classicActionBtn} title="Block" aria-hidden="true">
              <BlockIcon />
              Block
            </span>
          </div>

          <div className={classes.classicActionGroup}>
            <span className={classes.classicActionBtn} title="Add Buddy" aria-hidden="true">
              <AddBuddyIcon />
              Add Buddy
            </span>
          </div>

          <span className={classes.classicActionSpacer} />

          <button
            type="button"
            className={classes.classicSendBtn}
            onClick={submit}
            disabled={!draft.trim()}
          >
            <SendMailIcon />
            Send
          </button>
        </div>
      </div>
    );
  }

  // --- Default (theme-aware) skin -----------------------------------------
  return (
    <div className={classes.root}>
      <div
        className={cx(classes.log, 'wm-no-drag')}
        ref={logRef}
        style={{ fontSize: msgFontSize }}
      >
        {messages.map((m) =>
          renderLine(m, {
            awayLine: classes.awayLine,
            awayTag: classes.awayTag,
            time: classes.time,
          })
        )}
      </div>

      <div className={classes.typing} aria-live="polite">
        {typing ? `${partnerName} is typing…` : ''}
      </div>

      <div className={cx(classes.composeRow, 'wm-no-drag')}>
        <textarea
          ref={inputRef}
          className={classes.input}
          value={draft}
          placeholder={`Message ${partnerName}…`}
          rows={1}
          onChange={(e) => {
            setDraft(e.target.value);
            if (e.target.value.trim()) signalTyping();
            else stopTyping();
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={classes.sendBtn}
          onClick={submit}
          disabled={!draft.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ImConversation;
